/**
 * Tool Call Card
 *
 * Renders an inline expandable tool call with input and result.
 * - Shows tool name, expandable input JSON
 * - If result exists, shows result JSON
 */

import { useState } from 'react'
import type { CSSProperties } from 'react'
import { ChevronDown } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import type { ToolCall, ToolResult } from '../types'

export function ToolCallCard({
  toolCall,
  result,
}: {
  toolCall: ToolCall
  result?: ToolResult
}) {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div style={styles.container}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setIsExpanded(!isExpanded)}
        style={styles.button}
        data-testid={`tool-call-${toolCall.id}`}
      >
        <ChevronDown
          style={{
            ...styles.chevron,
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
          className="transition-transform"
        />
        <span style={styles.toolName}>{toolCall.name}</span>
      </Button>

      {isExpanded && (
        <div style={styles.expandedContent}>
          <div>
            <p style={styles.sectionLabel}>Input:</p>
            <pre style={styles.codeBlock}>
              {JSON.stringify(toolCall.input, null, 2)}
            </pre>
          </div>

          {result && (
            <div>
              <p style={styles.sectionLabel}>Result:</p>
              <pre style={styles.codeBlock}>
                {JSON.stringify(result.output, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const styles: Record<string, CSSProperties> = {
  container: {
    borderRadius: 'var(--radius-sm)',
    border: '1px solid rgba(128, 128, 128, 0.2)',
    background: 'rgba(0, 0, 0, 0.05)',
    padding: '8px',
  },
  button: {
    width: '100%',
    justifyContent: 'flex-start',
    fontSize: '12px',
  },
  chevron: {
    width: '12px',
    height: '12px',
    marginRight: '8px',
  },
  toolName: {
    fontFamily: 'monospace',
    fontWeight: 600,
  },
  expandedContent: {
    marginTop: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    borderTop: '1px solid rgba(128, 128, 128, 0.2)',
    paddingTop: '8px',
  },
  sectionLabel: {
    fontSize: '12px',
    fontWeight: 600,
    opacity: 0.75,
  },
  codeBlock: {
    marginTop: '4px',
    overflowX: 'auto',
    borderRadius: 'var(--radius-sm)',
    background: 'rgba(0, 0, 0, 0.05)',
    padding: '8px',
    fontSize: '12px',
  },
}
